'use client';

import React, { useState, useEffect, useContext } from 'react';
import AppLayout from '@/components/AppLayout';
import { LayoutDashboard, Phone, Target, CheckCircle, Loader2, RefreshCw, Clock, Star, ChevronRight, Bell, PhoneCall, MessageSquare, Plus, TrendingUp, TrendingDown, Minus, PhoneMissed, Timer } from 'lucide-react';
import Link from 'next/link';
import { AuthContext } from '@/context/AuthContext';
import Icon from '@/components/ui/AppIcon';



interface Operator {
  id: string;
  name: string;
  initials: string;
  status: string;
  role: string;
}

interface KPIs {
  callsToday: number;
  avgWaitTime: string;
  avgWaitSeconds: number;
  resolvedPercent: number;
  qualityScore: number;
  missedToday: number;
  resolvedToday: number;
}

interface RecentCall {
  id: string;
  callerNumber: string;
  patientName?: string;
  status: string;
  duration?: string;
  date: string;
  time?: string;
  reason?: string;
  outcome?: string | null;
  waitTime?: string | null;
}

interface Goal {
  id?: string;
  label?: string;
  target?: number;
  current?: number;
  unit?: string;
}

interface DashboardData {
  operator: Operator;
  kpis: KPIs;
  recentCalls: RecentCall[];
  goals: Goal[];
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { label: string; cls: string }> = {
    completed: { label: 'Completata', cls: 'bg-green-100 text-green-700' },
    missed: { label: 'Persa', cls: 'bg-red-100 text-red-700' },
    inbound: { label: 'In entrata', cls: 'bg-blue-100 text-blue-700' },
    outbound: { label: 'In uscita', cls: 'bg-purple-100 text-purple-700' },
    transferred: { label: 'Trasferita', cls: 'bg-amber-100 text-amber-700' },
    voicemail: { label: 'Voicemail', cls: 'bg-gray-100 text-gray-600' },
  };
  const s = map[status] ?? { label: status, cls: 'bg-gray-100 text-gray-600' };
  return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${s.cls}`}>{s.label}</span>;
}

function KpiCard({ label, value, icon: Icon, color, bg, trend }: {
  label: string; value: string | number; icon: React.ElementType;
  color: string; bg: string; trend?: 'up' | 'down' | 'neutral';
}) {
  return (
    <div className="bg-white rounded-2xl border border-border p-4 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-xl ${bg} flex items-center justify-center shrink-0`}>
        <Icon size={18} className={color} />
      </div>
      <div className="flex-1 min-w-0">
        <p className={`text-2xl font-bold ${color}`}>{value}</p>
        <p className="text-xs text-muted-foreground leading-tight">{label}</p>
      </div>
      {trend && (
        <div className="shrink-0">
          {trend === 'up' && <TrendingUp size={14} className="text-green-500" />}
          {trend === 'down' && <TrendingDown size={14} className="text-red-500" />}
          {trend === 'neutral' && <Minus size={14} className="text-muted-foreground" />}
        </div>
      )}
    </div>
  );
}

export default function OperatorDashboardPage() {
  const { currentOperatorId, isLoading: authLoading } = useContext(AuthContext);
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  const load = async (operatorId: string | null) => {
    if (!operatorId) {
      setError(true);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(false);
    try {
      const res = await fetch(`/api/operators/my-dashboard?operatorId=${operatorId}`);
      if (!res.ok) throw new Error('API error');
      const json = await res.json();
      setData(json ?? null);
    } catch {
      setError(true);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!authLoading) {
      load(currentOperatorId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, currentOperatorId]);

  if (authLoading || loading) return (
    <AppLayout>
      <div className="flex items-center justify-center h-full py-32" role="status" aria-label="Caricamento dashboard operatore">
        <div className="text-center space-y-3">
          <Loader2 size={28} className="animate-spin text-primary mx-auto" aria-hidden="true" />
          <p className="text-sm text-muted-foreground">Caricamento dashboard…</p>
        </div>
      </div>
    </AppLayout>
  );

  if (error || !data) return (
    <AppLayout>
      <div className="flex items-center justify-center h-full py-32" role="alert">
        <div className="text-center space-y-3">
          <LayoutDashboard size={36} className="mx-auto text-muted-foreground/30" aria-hidden="true" />
          <p className="text-sm font-medium text-foreground">Dashboard non disponibile</p>
          <p className="text-xs text-muted-foreground">
            {!currentOperatorId
              ? 'Sessione non trovata. Effettua il login.' :'Impossibile caricare i dati dell\'operatore'}
          </p>
          {currentOperatorId ? (
            <button
              onClick={() => load(currentOperatorId)}
              className="mt-2 inline-flex items-center gap-2 px-4 py-2 bg-primary text-white text-sm rounded-xl hover:bg-primary/90 transition-colors min-h-[44px] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
            >
              <RefreshCw size={14} aria-hidden="true" /> Riprova
            </button>
          ) : (
            <Link
              href="/login"
              className="mt-2 inline-flex items-center gap-2 px-4 py-2 bg-primary text-white text-sm rounded-xl hover:bg-primary/90 transition-colors min-h-[44px]"
            >
              Vai al Login
            </Link>
          )}
        </div>
      </div>
    </AppLayout>
  );

  const { operator, kpis, recentCalls, goals } = data;
  const qualityColor = kpis.qualityScore >= 80 ? 'text-green-600' : kpis.qualityScore >= 60 ? 'text-amber-600' : 'text-red-600';
  const qualityBg = kpis.qualityScore >= 80 ? 'bg-green-100' : kpis.qualityScore >= 60 ? 'bg-amber-100' : 'bg-red-100';

  return (
    <AppLayout>
      <div className="min-h-screen bg-background">
        {/* Toast */}
        {toast && (
          <div
            role="alert"
            aria-live="assertive"
            aria-atomic="true"
            className="fixed top-4 right-4 z-50 bg-green-600 text-white px-4 py-3 rounded-xl shadow-lg text-sm font-medium animate-in slide-in-from-top-2"
          >
            {toast}
          </div>
        )}

        {/* Header */}
        <div className="bg-white border-b border-border px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-primary/10 flex items-center justify-center ring-2 ring-primary/20">
                <span className="text-sm font-bold text-primary">{operator.initials}</span>
              </div>
              <div>
                <h1 className="text-lg font-bold text-foreground">Ciao, {operator.name}! 👋</h1>
                <p className="text-xs text-muted-foreground">
                  {operator.role} · {new Date().toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {/* Status badge */}
              <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border ${
                operator.status === 'online' ? 'bg-green-50 border-green-200 text-green-700' :
                operator.status === 'busy'? 'bg-amber-50 border-amber-200 text-amber-700' : 'bg-gray-50 border-gray-200 text-gray-600'
              }`}>
                <span className={`w-1.5 h-1.5 rounded-full ${
                  operator.status === 'online' ? 'bg-green-500' :
                  operator.status === 'busy' ? 'bg-amber-500' : 'bg-gray-400'
                }`} />
                {operator.status === 'online' ? 'Online' : operator.status === 'busy' ? 'Occupato' : 'Offline'}
              </span>
              <button
                onClick={() => load(currentOperatorId)}
                className="p-2 border border-border rounded-xl hover:bg-muted transition-colors min-h-[44px] min-w-[44px] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                aria-label="Aggiorna dashboard"
                title="Aggiorna"
              >
                <RefreshCw size={14} className="text-muted-foreground" aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>

        <div className="px-6 lg:px-8 py-6 space-y-6 max-w-7xl mx-auto">

          {/* Quick Actions */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: 'Nuova Chiamata', icon: PhoneCall, href: '/call-dashboard', color: 'text-primary', bg: 'bg-primary/10 hover:bg-primary/20' },
              { label: 'Invia SMS', icon: MessageSquare, href: '/sms', color: 'text-blue-600', bg: 'bg-blue-50 hover:bg-blue-100' },
              { label: 'Follow-up', icon: Bell, href: '/follow-ups', color: 'text-amber-600', bg: 'bg-amber-50 hover:bg-amber-100' },
              { label: 'Nuovo Paziente', icon: Plus, href: '/patients', color: 'text-green-600', bg: 'bg-green-50 hover:bg-green-100' },
            ].map(a => (
              <Link
                key={a.label}
                href={a.href}
                className={`flex items-center gap-2.5 px-4 py-3 rounded-xl border border-border ${a.bg} transition-colors`}
              >
                <a.icon size={16} className={a.color} />
                <span className="text-sm font-medium text-foreground">{a.label}</span>
              </Link>
            ))}
          </div>

          {/* KPI Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <KpiCard label="Chiamate oggi" value={kpis.callsToday} icon={Phone} color="text-primary" bg="bg-primary/10" />
            <KpiCard label="Risolte" value={kpis.resolvedToday} icon={CheckCircle} color="text-green-600" bg="bg-green-100" />
            <KpiCard label="Qualità" value={`${kpis.qualityScore}%`} icon={Star} color={qualityColor} bg={qualityBg} />
            <KpiCard label="Perse" value={kpis.missedToday} icon={PhoneMissed} color="text-red-600" bg="bg-red-100" />
          </div>

          {/* Secondary KPIs */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-white rounded-2xl border border-border p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center shrink-0">
                <Timer size={18} className="text-purple-600" />
              </div>
              <div>
                <p className="text-xl font-bold text-purple-600">{kpis.avgWaitTime}</p>
                <p className="text-xs text-muted-foreground">Attesa media</p>
              </div>
            </div>
            <div className="bg-white rounded-2xl border border-border p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-teal-100 flex items-center justify-center shrink-0">
                <TrendingUp size={18} className="text-teal-600" />
              </div>
              <div>
                <p className="text-xl font-bold text-teal-600">{kpis.resolvedPercent}%</p>
                <p className="text-xs text-muted-foreground">Tasso risoluzione</p>
              </div>
            </div>
            <div className="bg-white rounded-2xl border border-border p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center shrink-0">
                <Target size={18} className="text-indigo-600" />
              </div>
              <div>
                <p className="text-xl font-bold text-indigo-600">
                  {kpis.callsToday > 0 ? Math.round(((kpis.callsToday - kpis.missedToday) / kpis.callsToday) * 100) : 0}%
                </p>
                <p className="text-xs text-muted-foreground">Tasso risposta</p>
              </div>
            </div>
          </div>

          {/* Goals + Recent Calls */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

            {/* Daily Goals */}
            <div className="bg-white rounded-2xl border border-border p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                  <Target size={15} className="text-primary" /> Obiettivi del Giorno
                </h3>
                <Link href="/daily-goals" className="text-xs text-primary hover:underline">Configura</Link>
              </div>
              {goals.length === 0 ? (
                <div className="text-center py-8 space-y-2">
                  <Target size={28} className="mx-auto text-muted-foreground/30" />
                  <p className="text-sm text-muted-foreground">Nessun obiettivo configurato</p>
                  <Link href="/daily-goals" className="text-xs text-primary hover:underline">Aggiungi obiettivi →</Link>
                </div>
              ) : (
                <div className="space-y-4">
                  {goals.slice(0, 4).map((g, i) => {
                    const pct = g.target && g.current != null ? Math.min(Math.round((g.current / g.target) * 100), 100) : 0;
                    const done = pct >= 100;
                    return (
                      <div key={g.id ?? i}>
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-xs font-medium text-foreground">{g.label ?? `Obiettivo ${i + 1}`}</span>
                          <span className="text-xs text-muted-foreground">
                            {g.current ?? 0} / {g.target ?? '—'} {g.unit ?? ''}
                          </span>
                        </div>
                        <div className="w-full bg-muted rounded-full h-2.5">
                          <div
                            className={`h-2.5 rounded-full transition-all ${done ? 'bg-green-500' : 'bg-primary'}`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        {done && (
                          <p className="text-xs text-green-600 font-medium mt-0.5 flex items-center gap-1">
                            <CheckCircle size={11} /> Completato!
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Recent Calls */}
            <div className="bg-white rounded-2xl border border-border p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                  <Clock size={15} className="text-muted-foreground" /> Chiamate Recenti
                </h3>
                <Link href="/call-history" className="text-xs text-primary hover:underline">Vedi tutte</Link>
              </div>
              {recentCalls.length === 0 ? (
                <div className="text-center py-8 space-y-2">
                  <Phone size={28} className="mx-auto text-muted-foreground/30" />
                  <p className="text-sm text-muted-foreground">Nessuna chiamata oggi</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {recentCalls.slice(0, 5).map(call => (
                    <Link
                      key={call.id}
                      href={`/calls/${call.id}`}
                      className="flex items-center gap-3 p-3 rounded-xl hover:bg-muted/40 transition-colors group"
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                        call.status === 'missed' ? 'bg-red-100' : 'bg-primary/10'
                      }`}>
                        {call.status === 'missed'
                          ? <PhoneMissed size={14} className="text-red-600" />
                          : <Phone size={14} className="text-primary" />
                        }
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">
                          {call.patientName ?? call.callerNumber}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {call.time ?? ''} {call.duration ? `· ${call.duration}` : ''}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <StatusBadge status={call.status} />
                        <ChevronRight size={13} className="text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Quality Score Detail */}
          <div className="bg-white rounded-2xl border border-border p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                <Star size={15} className="text-amber-500" /> Punteggio Qualità
              </h3>
              <Link href="/operator-sla" className="text-xs text-primary hover:underline">Dettagli SLA</Link>
            </div>
            <div className="flex items-center gap-6">
              <div className="relative w-20 h-20 shrink-0">
                <svg className="w-20 h-20 -rotate-90" viewBox="0 0 36 36">
                  <circle cx="18" cy="18" r="15.9" fill="none" stroke="#e5e7eb" strokeWidth="3" />
                  <circle
                    cx="18" cy="18" r="15.9" fill="none"
                    stroke={kpis.qualityScore >= 80 ? '#22c55e' : kpis.qualityScore >= 60 ? '#f59e0b' : '#ef4444'}
                    strokeWidth="3"
                    strokeDasharray={`${kpis.qualityScore} ${100 - kpis.qualityScore}`}
                    strokeLinecap="round"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className={`text-lg font-bold ${qualityColor}`}>{kpis.qualityScore}</span>
                </div>
              </div>
              <div className="flex-1 grid grid-cols-2 gap-3">
                {[
                  { label: 'Chiamate gestite', value: kpis.callsToday, good: kpis.callsToday > 0 },
                  { label: 'Chiamate risolte', value: kpis.resolvedToday, good: kpis.resolvedToday > 0 },
                  { label: 'Chiamate perse', value: kpis.missedToday, good: kpis.missedToday === 0 },
                  { label: 'Tasso risoluzione', value: `${kpis.resolvedPercent}%`, good: kpis.resolvedPercent >= 70 },
                ].map(item => (
                  <div key={item.label} className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full shrink-0 ${item.good ? 'bg-green-500' : 'bg-red-400'}`} />
                    <div>
                      <p className="text-xs text-muted-foreground">{item.label}</p>
                      <p className="text-sm font-semibold text-foreground">{item.value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

        </div>
      </div>
    </AppLayout>
  );
}
